import { useQuery } from '@tanstack/vue-query'
import { toValue, type MaybeRefOrGetter } from 'vue'
import { orderKeys } from '../orderQueryKeys'
import { getOrderDocuments } from '../orderEndpoints'
import type { OrderDocument } from '../../types/orderTypes'

/**
 * The order's documents list (small JSON — fetched on page load so the page
 * knows whether to show the title deed row). `staleTime: 0` so every visit
 * re-reads it; the caller decides whether and when to poll.
 */
export function useOrderDocumentsQuery(
  orderId: MaybeRefOrGetter<string>,
  refetchInterval: (documents: OrderDocument[] | undefined) => number | false,
) {
  return useQuery({
    queryKey: () => orderKeys.documents(toValue(orderId)),
    queryFn: () => getOrderDocuments(toValue(orderId)),
    enabled: () => Boolean(toValue(orderId)),
    staleTime: 0,
    // Evaluated after every response, so the poll can stop as soon as the
    // certificate lands or the window closes.
    refetchInterval: (query) => refetchInterval(query.state.data),
    // An empty list is the expected answer for a fresh order; retrying a
    // failed call is the poll's job, not the retry policy's.
    retry: false,
  })
}
